/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import BreakdownSites from '../../../../shared/BreakdownSites/BreakdownSites';
import {
  EmaasSubscriptionType,
  EnergyUsageField
} from '../../../../shared/Enums';
import './EnergyUsageComponent.scss';
import { IEmaaSDataProps } from '../../IPortfolioProps';
import {
  convertDataForEnergyUsage,
  getSiteListForEnergy,
  isPremiumSubscription
} from '../../../../shared/Utils/portfolioDataFormatter';
import { IMSEMaaSPortfolioFormattedData } from '../../IEnergyUsagePortfolioProps';
import { ErrorContainer } from '@bmsp/bmsp.web.component.common';
import {
  apiNoContentStatusCode,
  MAGIC_VALUE
} from '../../../../shared/Utils/Constants';
import ProgressBarWidget from '../../../../shared/ProgressBar/ProgressBarWidget';
import { IDataTableHeader } from '../../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { ApiContext } from '../../../../../context/ApiContext';
import { Loader } from '@scuf/common';
import BreakdownSitesForCarbonEnergy from '../../../../shared/BreakdownSites/BreakdownSitesForCarbonEnergy';
import { ConversionType } from '../../../../../main';

export const EnergyUsageComponent = (props: IEmaaSDataProps) => {
  const {
    data: { emaasEnergyDataList, energyErrors },
    visibleSites,
    onShowMore,
    maxRecords
  } = props;
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;

  const [loader, setLoader] = React.useState(true);
  const [
    checkAllSitesAndMonthData,
    setCheckAllSitesAndMonthData
  ] = React.useState(true);

  const checkIfAllSitesHasCompleteData = (data: any) => {
    return data && data.every((item: any) => item.site.isCompleted);
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data[0].monthWiseAggregations.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data[0].monthWiseAggregations.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalBaselineConsumption !== 0 &&
          item.totalBaselineConsumption !== null &&
          item.totalConsumption !== 0 &&
          item.totalConsumption !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.every(
        (item: any) =>
          item.site.totalConsumption !== 0 &&
          item.site.totalBaselineConsumption !== 0 &&
          item.site.totalConsumption !== null &&
          item.site.totalBaselineConsumption !== null
      )
    );
  };

  useEffect(() => {
    if (emaasEnergyDataList && emaasEnergyDataList.length > 0) {
      setCheckAllSitesAndMonthData(
        checkIfAllSitesHasPreviousPeriod(emaasEnergyDataList) &&
          checkIfAllMonthsHasPreviousPeriod(emaasEnergyDataList) &&
          checkIfAllMonthHasCompleteData(emaasEnergyDataList) &&
          checkIfAllSitesHasCompleteData(emaasEnergyDataList)
      );
      setLoader(false);
    }
  }, [emaasEnergyDataList]);

  useEffect(() => {
    if (energyErrors && energyErrors !== null) {
      setLoader(false);
    }
  }, [energyErrors]);

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  //Show error based on status code
  if (energyErrors && energyErrors.status) {
    if (energyErrors.status === apiNoContentStatusCode) {
      return (
        <div className="emaas-no-content-error">{t(d.NODATAAVAILABLE)}</div>
      );
    }
    return (
      <div className="emaas-no-content-error">
        <ErrorContainer
          headerText={'Loading Error'}
          errorDescription={
            'Refresh the page to load the data. Contact your administrator if the problem persists.'
          }
        />
      </div>
    );
  } else {
    let siteConsumptionData: IMSEMaaSPortfolioFormattedData = {
      tableData: [],
      progressBarData: undefined,
      displayUnit: ''
    };
    let siteList = getSiteListForEnergy(emaasEnergyDataList!);

    if (siteList && siteList.length > 0) {
      siteConsumptionData = convertDataForEnergyUsage(
        siteList,
        fromUnit, //emaasEnergyDataList![0].energyMeasurementUnit,
        visibleSites,
        isPremiumSubscription(emaasEnergyDataList!),
        emaasEnergyDataList![0].energyMeasurementUnit,
        apiContext.showChangeWhenData
      );
    }

    const checkBasicSub = emaasEnergyDataList!.every((subs) => {
      return subs?.subscriptionType?.toLowerCase() ===
        EnergyUsageField.BASICSUBS.toLowerCase()
        ? true
        : false;
    });

    if (siteConsumptionData) {
      const {
        tableData,
        progressBarData,
        displayUnit,
        deviationScaledData
      } = siteConsumptionData;

      if (tableData && progressBarData) {
        const {
          actual,
          previousPeriod,
          deviation,
          deviationPercentage,
          absolutePreviousPeriod,
          absoluteActual,
          actualUnit,
          baselineUnit,
          changeUnit
        } = progressBarData;

        const aggregationEnergyUsageData = {
          actual: actual,
          previousPeriod: previousPeriod,
          savings: checkAllSitesAndMonthData ? deviation! : null,
          savingsPercentage: checkAllSitesAndMonthData
            ? deviationPercentage!
            : null,
          absoluteActual: absoluteActual,
          absolutePrevPeriod: absolutePreviousPeriod,
          actualUnit: actualUnit,
          baselineUnit: baselineUnit,
          changeUnit: changeUnit
        };

        const headers = [
          {
            HeaderValue: t(d.SITENAMESHEADER),
            TextAlignValue: 'left',
            Field: 'SiteName',
            initialWidth: '35%'
          },
          {
            HeaderValue: t(d.ENERGYNOUNIT),
            TextAlignValue: 'right',
            Field: 'Value',
            sortable: true,
            initialWidth: '25%'
          },
          {
            HeaderValue: t(d.CHANGE),
            TextAlignValue: 'right',
            Field: 'Deviation',
            sortable: true,
            initialWidth: '25%'
          }
        ];
        return loader ? (
          <Loader />
        ) : (
          <div className="kpi-card-flex-container">
            <ProgressBarWidget
              unit={displayUnit}
              category={EnergyUsageField.ENERGY_USAGE}
              aggregationData={aggregationEnergyUsageData}
              isProgressBarForEnergy={true}
              deviationScaledData={deviationScaledData}
              basicSub={checkBasicSub}
              fromCarbonKPI={true}
              originalUnit={emaasEnergyDataList![0].energyMeasurementUnit}
              conversionType={ConversionType.ELECTRICITY}
            />
            <div className="kpi-card-breakdown-site-container">
              <BreakdownSitesForCarbonEnergy
                columnHeaders={headers as IDataTableHeader[]}
                meterData={tableData}
                onShowMore={onShowMore}
                maxRecords={maxRecords}
                activeWidgetName={props.activeWidgetName}
                emissionMeasurementUnit={
                  emaasEnergyDataList![0].energyMeasurementUnit
                }
              />
            </div>
          </div>
        );
      }
    }
    return loader ? (
      <Loader />
    ) : (
      <div className="noDataDisplay ms-common-error-container">
        <ErrorContainer
          headerText={'Loading Error'}
          errorDescription={
            'Refresh the page to load the data. Contact your administrator if the problem persists.'
          }
        />
      </div>
    );
  }
};
export default EnergyUsageComponent;
